import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ShieldCheck, Copy, Zap } from "lucide-react";
import { VersionTag } from "@/components/VersionTag";

const BADGE_VARIANT_BY_STATUS = {
  SetupOnNextRun: "outline",
  InProgress: "secondary",
  Success: "default",
  Error: "destructive",
};

const PHASE_LABEL = {
  finding: "Looking up domain…",
  creating: "Creating domain…",
  scanning: "Scanning website…",
  assigning: "Assigning consent policy…",
  publishing: "Publishing…",
  awaiting_publish: "Waiting for publish to complete…",
  reading: "Reading domain settings…",
};

// TEMP DISABLE — OneTrust tenant is LIVE; queueing a dealer here means it'll
// be picked up (and actually provisioned) by the next feed import or an
// admin run-now. Flip back to true (and remove this comment) to re-enable;
// see the matching guard in queueSetup() below.
const QUEUE_SETUP_ENABLED = false;

// The real provisioning work happens server-side (onetrust_scheduler.py ticks
// every 5 minutes); this just re-reads OUR OWN dealer record periodically so
// the card doesn't sit frozen on a stale "InProgress" snapshot from the
// moment the button was clicked — it never calls OneTrust itself.
const POLL_INTERVAL_MS = 10000;

/** Our own OneTrust domain provisioning/reconciliation record — entirely
 * distinct from CookieManagerCard's feed-reported CookieManagerStatus (see
 * CookieConsentsLocal in models.py). */
export function CookieConsentsCard({ dealer, dealerId, onSaved, canManage = true }) {
  const { t } = useI18n();
  const [queueing, setQueueing] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const status = dealer.cookie_consents_local_status || null;
  const local = dealer.cookie_consents_local || {};
  const migrated = dealer.management_migrated_to_ddm === true;
  const categories = local.cookie_categories || [];

  useEffect(() => {
    if (status !== "InProgress") return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await client.get(`/dealers/${dealerId}/summary`);
        onSaved(data.dealer);
      } catch {
        // transient network/auth hiccup — next tick retries
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, dealerId]);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("dealership.cookieConsentsLocal.copied"));
    } catch {
      toast.error(t("dealership.cookieConsentsLocal.copyFailed"));
    }
  };

  const queueSetup = async () => {
    if (!QUEUE_SETUP_ENABLED) {
      toast.error(t("dealership.cookieConsentsLocal.queueSetupDisabled"));
      return;
    }
    setQueueing(true);
    try {
      // --- real call, commented out while disabled above ---
      // const { data } = await client.patch(`/dealers/${dealerId}/cookie-consents/setup`);
      // onSaved(data);
      // toast.success(t("dealership.cookieConsentsLocal.queued"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue setup");
    } finally {
      setQueueing(false);
    }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await client.post(`/dealers/${dealerId}/cookie-consents/run-now`);
      onSaved(data);
      toast.success(t("dealership.cookieConsentsLocal.runNowStarted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start OneTrust provisioning");
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <Card className="mb-6 p-6" data-testid="cookie-consents-card">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <h2 className="flex items-center gap-1.5 text-lg font-semibold">{t("dealership.cookieConsentsLocal.title")} <VersionTag version={dealer.card_versions?.onetrust} testId="version-tag-onetrust" /></h2>
        {status ? (
          <Badge variant={BADGE_VARIANT_BY_STATUS[status] || "secondary"} data-testid="cookie-consents-status-badge">
            {t(`dealership.cookieConsentsLocal.status.${status}`)}
          </Badge>
        ) : (
          <Badge variant="outline" data-testid="cookie-consents-status-badge">—</Badge>
        )}
        {status === "InProgress" && local.provision_phase && (
          <span className="text-xs text-muted-foreground" data-testid="cookie-consents-phase">
            {PHASE_LABEL[local.provision_phase] || local.provision_phase}
          </span>
        )}

        {canManage && (
          <div className="ml-auto flex items-center gap-2">
            <Button
              data-testid="cookie-consents-queue-setup"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={!migrated || queueing || status === "InProgress"}
              title={!migrated ? t("dealership.cookieConsentsLocal.mustBeMigratedHint") : undefined}
              onClick={queueSetup}
            >
              {t("dealership.cookieConsentsLocal.queueSetup")}
            </Button>
            <Button
              data-testid="cookie-consents-run-now"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={!migrated || runningNow || status === "InProgress"}
              title={!migrated ? t("dealership.cookieConsentsLocal.mustBeMigratedHint") : undefined}
              onClick={runNow}
            >
              <Zap className="h-3.5 w-3.5" />
              {t("dealership.cookieConsentsLocal.runNow")}
            </Button>
          </div>
        )}
      </div>

      {status === "Error" && local.last_error && (
        <p className="mb-4 rounded-md bg-destructive/10 p-3 text-xs text-destructive" data-testid="cookie-consents-error">
          {local.last_error}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={t("dealership.cookieConsentsLocal.domainId")} value={local.onetrust_domain_id} testId="domain-id" />
        <Field
          label={t("dealership.cookieConsentsLocal.domainSource")}
          value={
            local.onetrust_domain_id
              ? t(local.domain_created_by_us
                ? "dealership.cookieConsentsLocal.domainSourceProvisioned"
                : "dealership.cookieConsentsLocal.domainSourcePreexisting")
              : ""
          }
          testId="domain-source"
        />
        <Field label={t("dealership.cookieConsentsLocal.publishStatus")} value={local.publish_status} testId="publish-status" />
        <Field label={t("dealership.cookieConsentsLocal.lastSyncedAt")} value={local.last_synced_at} testId="last-synced-at" />
        <ScriptField label={t("dealership.cookieConsentsLocal.scriptId")} value={local.script_id} onCopy={copy} testId="script-id" />
        <ScriptField label={t("dealership.cookieConsentsLocal.scriptIdTest")} value={local.script_id_test} onCopy={copy} testId="script-id-test" />
      </div>

      {categories.length > 0 && (
        <div className="mt-6 border-t border-border pt-4">
          <p className="data-label mb-2">{t("dealership.cookieConsentsLocal.categories")}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cookie-consents-categories-table">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="pb-1 pr-4 font-medium">{t("dealership.cookieConsentsLocal.categoryName")}</th>
                  <th className="pb-1 pr-4 font-medium">{t("dealership.cookieConsentsLocal.categoryCode")}</th>
                  <th className="pb-1 font-medium">{t("dealership.cookieConsentsLocal.categoryStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c, i) => (
                  <tr key={c.id || i} className="border-t border-border/50">
                    <td className="py-1.5 pr-4">{c.name}</td>
                    <td className="py-1.5 pr-4 font-mono text-xs">{c.custom_category_name}</td>
                    <td className="py-1.5 text-xs text-muted-foreground">{c.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function Field({ label, value, testId }) {
  return (
    <div>
      <p className="data-label mb-1">{label}</p>
      <p className="text-sm" data-testid={`cookie-consents-${testId}`}>{value || "—"}</p>
    </div>
  );
}

function ScriptField({ label, value, onCopy, testId }) {
  return (
    <div>
      <p className="data-label mb-1">{label}</p>
      {value ? (
        <div className="flex items-center gap-1.5">
          <p className="truncate font-mono text-xs" data-testid={`cookie-consents-${testId}`}>{value}</p>
          <button
            type="button"
            data-testid={`cookie-consents-${testId}-copy`}
            className="text-muted-foreground hover:text-foreground"
            onClick={() => onCopy(value)}
          >
            <Copy className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid={`cookie-consents-${testId}`}>—</p>
      )}
    </div>
  );
}
